import React from 'react';
import { motion } from 'framer-motion';
import { highlightTerms } from '../highlightTerms';

export const HierarchySlide = ({ slide }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.25, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div
      className="h-full w-full flex flex-col justify-center items-center px-8 md:px-12 lg:px-20 py-12"
      data-testid={`slide-${slide.id}`}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl w-full"
      >
        <motion.h2
          variants={itemVariants}
          className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-10 text-center"
        >
          {highlightTerms(slide.title)}
        </motion.h2>

        {/* Pyramid - single SVG-aligned structure */}
        <div className="relative w-full max-w-[700px] mx-auto" style={{ aspectRatio: '7/5' }}>
          {/* Background pyramid shape */}
          <div
            className="absolute inset-0 z-0"
            style={{
              clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
              background: 'linear-gradient(180deg, rgba(42,29,69,0.5) 0%, rgba(211,17,19,0.2) 100%)',
            }}
          />

          {/* Top tier - Technique */}
          <motion.div
            variants={itemVariants}
            className="absolute z-10 flex items-center justify-center text-center"
            style={{ top: '8%', left: '33%', width: '34%', height: '25%' }}
          >
            <div>
              <h3 className="pres-font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                TECHNIQUE
              </h3>
              <p className="font-sans text-sm md:text-base text-gray-400 mt-1">
                Application
              </p>
            </div>
          </motion.div>

          {/* Divider line between top and middle */}
          <div
            className="absolute z-10"
            style={{
              top: '37%',
              left: '22%',
              width: '56%',
              height: '1px',
              background: 'rgba(255,255,255,0.15)',
            }}
          />

          {/* Middle tier - RFD & Elasticity */}
          <motion.div
            variants={itemVariants}
            className="absolute z-10 flex"
            style={{ top: '38%', left: '18%', width: '64%', height: '27%' }}
          >
            <div className="flex-1 flex items-center justify-center text-center border-r border-white/10">
              <div>
                <h3 className="pres-font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                  RFD
                </h3>
                <p className="font-sans text-sm md:text-base text-gray-400 mt-1">
                  {highlightTerms("POWER development")}
                </p>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center text-center">
              <div>
                <h3 className="pres-font-heading text-2xl md:text-3xl font-bold text-white uppercase">
                  ELASTICITY
                </h3>
                <p className="font-sans text-sm md:text-base text-gray-400 mt-1">
                  {highlightTerms("FORCE reuse")}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Base tier - Strength */}
          <motion.div
            variants={itemVariants}
            className="absolute z-10"
            style={{ bottom: '0', left: '0', width: '100%', height: '35%' }}
          >
            <div
              className="w-full h-full flex items-center justify-center text-center shadow-lg shadow-[#D31113]/30"
              style={{
                clipPath: 'polygon(17.5% 0%, 82.5% 0%, 100% 100%, 0% 100%)',
                background: '#D31113',
              }}
            >
              <div>
                <h3 className="pres-font-heading text-3xl md:text-4xl font-bold text-white uppercase">
                  STRENGTH
                </h3>
                <p className="font-sans text-sm md:text-base text-white/80 mt-1">
                  {highlightTerms("FORCE production — The Foundation")}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Pyramid edge lines */}
          <svg className="absolute inset-0 z-20 w-full h-full pointer-events-none" viewBox="0 0 700 500" preserveAspectRatio="none">
            {/* Left edge */}
            <line x1="350" y1="0" x2="0" y2="500" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            {/* Right edge */}
            <line x1="350" y1="0" x2="700" y2="500" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
            {/* Bottom edge */}
            <line x1="0" y1="500" x2="700" y2="500" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
          </svg>
        </div>

        {slide.keyLine && (
          <motion.div
            variants={itemVariants}
            className="mt-10 text-center"
          >
            <p className="font-sans text-xl md:text-2xl text-white font-semibold italic">
              "{highlightTerms(slide.keyLine)}"
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};
